import type { IncidentCaseV2Step } from '@lmc/validations'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'
import { useEffect, useRef } from 'react'

export type IncidentCaseV2ExitVia = 'cancel' | 'closeIncident' | 'finish'

export type IncidentCaseV2StepVia =
  | 'back'
  | 'next'
  | 'resume'
  | 'start'
  | 'stepper'

type UseIncidentCaseV2JourneyArgs = {
  /** Left out until the case exists. */
  incidentReportId?: string
  isResuming: boolean
  step: IncidentCaseV2Step
}

/**
 * The form's journey events. They never carry the case's content; the backend
 * sends opened, step saved and closed.
 */
const useIncidentCaseV2Journey = ({
  incidentReportId,
  isResuming,
  step
}: UseIncidentCaseV2JourneyArgs) => {
  const sendEvent = useMixpanelEvent()
  const stepVia = useRef<IncidentCaseV2StepVia>(isResuming ? 'resume' : 'start')
  const viewedStep = useRef<IncidentCaseV2Step | null>(null)
  const sendExit = useRef<(() => void) | null>(null)

  useEffect(() => {
    if (viewedStep.current === step) {
      return
    }
    viewedStep.current = step
    // Moving on after a cancelled leave prompt means the user stayed.
    sendExit.current = null
    sendEvent(MixpanelEvents.INCIDENT_CASE_STEP_VIEWED, {
      ...(incidentReportId && { incidentReportId }),
      step,
      via: stepVia.current
    })
  }, [incidentReportId, sendEvent, step])

  // Sent once the form is gone, so a leave prompt the user cancels sends none.
  useEffect(() => () => sendExit.current?.(), [])

  const markExit = (via: IncidentCaseV2ExitVia, hadUnsavedChanges: boolean) => {
    sendExit.current = () =>
      sendEvent(MixpanelEvents.INCIDENT_CASE_EXITED, {
        hadUnsavedChanges,
        ...(incidentReportId && { incidentReportId }),
        step,
        via
      })
  }

  const clearExit = () => {
    sendExit.current = null
  }

  const setStepVia = (via: IncidentCaseV2StepVia) => {
    stepVia.current = via
  }

  return { clearExit, markExit, setStepVia }
}

export default useIncidentCaseV2Journey
